import { MusicItem, MusicOverlay, MusicStatus, MusicTier } from '../api/payment/music-request.api';

// Hàm thuần cho "Phát nhạc theo yêu cầu" — nhãn trạng thái, định dạng thời gian, kiểm link
// YouTube nhanh phía client, chọn hành động overlay. Tách khỏi component để test không phải
// dựng React.

/** Trạng thái một bài yêu cầu ⇒ nhãn tiếng Việt hiện trên khối gửi bài / bảng điều khiển. */
export const musicStatusLabel = (s: MusicStatus): string => {
  switch (s) {
    case 'Pending':
      return 'Chờ duyệt';
    case 'Approving':
      return 'Đang duyệt';
    case 'Queued':
      return 'Trong hàng chờ';
    case 'Playing':
      return 'Đang phát';
    case 'Played':
      return 'Đã phát';
    case 'Skipped':
      return 'Đã bỏ qua';
    case 'Rejected':
      return 'Bị từ chối';
    case 'Failed':
      return 'Không phát được';
    case 'Expired':
      return 'Hết hạn chờ';
    default:
      return s;
  }
};

/** Số giây ⇒ "phút:giây" (vd 75 ⇒ "1:15"). Âm hoặc không phải số ⇒ "0:00". */
export const formatMmSs = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${mm}:${`${ss}`.padStart(2, '0')}`;
};

/** Kiểm nhanh phía client (host youtube.com/youtu.be) trước khi gọi API preview tốn round-trip. */
export const looksLikeYouTubeUrl = (s: string): boolean => {
  if (!s) return false;
  try {
    const u = new URL(s.trim());
    const host = u.hostname.toLowerCase().replace(/^www\./, '');
    return (
      host === 'youtube.com' ||
      host === 'm.youtube.com' ||
      host === 'music.youtube.com' ||
      host === 'youtu.be'
    );
  } catch {
    return false;
  }
};

export type OverlayAction = { kind: 'play'; item: MusicItem } | { kind: 'start'; item: MusicItem } | { kind: 'idle' };

/**
 * Có bài đang phát (`current`) ⇒ phát bài đó. Không có bài đang phát, chưa tạm dừng và còn
 * bài kế tiếp ⇒ báo overlay bắt đầu phát bài đầu hàng chờ. Còn lại (tạm dừng, hàng chờ trống,
 * chưa tải được trạng thái) ⇒ chờ.
 */
export const decideOverlayAction = (o: MusicOverlay | null): OverlayAction => {
  if (!o) return { kind: 'idle' };
  // Lớp phủ này không giữ quyền phát (đã có lớp phủ khác): không phát, không báo bắt đầu.
  if (o.isActiveInstance === false) return { kind: 'idle' };
  if (o.current) return { kind: 'play', item: o.current };
  if (!o.isPaused && o.next && o.next.length > 0) return { kind: 'start', item: o.next[0] };
  return { kind: 'idle' };
};

/** Mã trạng thái của YouTube IFrame Player (`YT.PlayerState`). */
export const YT_STATE = {
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  CUED: 5,
} as const;

/**
 * Overlay có nên báo "bài đã phát xong" chưa: video tự hết (state ENDED) hoặc đã phát tới độ
 * dài tối đa streamer cho phép. `maxSeconds` không hợp lệ (0, âm, NaN) thì chỉ dựa vào ENDED.
 */
export const shouldEndPlayback = (currentTime: number, maxSeconds: number, state: number): boolean => {
  if (state === YT_STATE.ENDED) return true;
  if (!Number.isFinite(maxSeconds) || maxSeconds <= 0) return false;
  return Number.isFinite(currentTime) && currentTime >= maxSeconds;
};

export type PlayerPlan =
  | { kind: 'load'; item: MusicItem }
  | { kind: 'keep' }
  | { kind: 'stop' }
  | { kind: 'start'; item: MusicItem; stopFirst: boolean }
  | { kind: 'none' };

/**
 * Từ hành động overlay + bài player đang giữ ⇒ việc player cần làm.
 * - `play` bài khác bài đang giữ (streamer bỏ qua bài cũ, hoặc vừa mở overlay) ⇒ nạp bài mới.
 * - `play` đúng bài đang giữ ⇒ để nguyên (không phát lại từ đầu mỗi lần tải lại trạng thái).
 * - `play` bài overlay đã báo xong nhưng máy chủ chưa kịp cập nhật ⇒ không phát lại, chỉ dừng.
 * - `start` ⇒ báo máy chủ bắt đầu bài đầu hàng chờ, dừng bài cũ nếu còn giữ.
 * - `idle` ⇒ dừng nếu đang giữ bài.
 */
export const planPlayer = (
  action: OverlayAction,
  loadedId: string | null,
  endedIds: ReadonlySet<string>
): PlayerPlan => {
  if (action.kind === 'play') {
    if (endedIds.has(action.item.id)) return loadedId ? { kind: 'stop' } : { kind: 'none' };
    if (loadedId === action.item.id) return { kind: 'keep' };
    return { kind: 'load', item: action.item };
  }
  if (action.kind === 'start') return { kind: 'start', item: action.item, stopFirst: loadedId !== null };
  return loadedId ? { kind: 'stop' } : { kind: 'none' };
};

/**
 * Overlay được mở lại giữa bài (OBS tải lại trang, mất mạng): tiếp tục từ chỗ đã phát theo
 * `startedAt` của máy chủ thay vì phát lại từ đầu, để tổng thời gian không vượt `maxSeconds`.
 * Lệch dưới 3 giây (vừa bắt đầu, lệch đồng hồ) coi như phát từ đầu.
 */
export const resumeOffsetSeconds = (startedAt: string | null, nowMs: number, maxSeconds: number): number => {
  if (!startedAt) return 0;
  const started = Date.parse(startedAt);
  if (!Number.isFinite(started)) return 0;
  const elapsed = (nowMs - started) / 1000;
  if (!Number.isFinite(elapsed) || elapsed < 3) return 0;
  if (Number.isFinite(maxSeconds) && maxSeconds > 0) return Math.min(elapsed, maxSeconds);
  return elapsed;
};

/** Máy chủ giữ quyền phát cho một lớp phủ 20 giây kể từ lần gọi gần nhất. */
export const OVERLAY_LEASE_MS = 20000;
/** Lớp phủ đang giữ quyền: gọi lại thường xuyên để giữ quyền (tối đa 8 giây một lần). */
export const OVERLAY_POLL_ACTIVE_MS = 8000;
/** Lớp phủ chưa giữ quyền: hỏi dày hơn để nhận quyền nhanh khi lớp phủ kia đóng. */
export const OVERLAY_POLL_INACTIVE_MS = 5000;

export const overlayPollMs = (o: MusicOverlay | null): number =>
  o && o.isActiveInstance === false ? OVERLAY_POLL_INACTIVE_MS : OVERLAY_POLL_ACTIVE_MS;

/**
 * Giờ máy chủ ước tính ở thời điểm `nowMs`: `serverNow` lúc nhận cộng thời gian đã trôi từ lúc
 * nhận (độ lệch đồng hồ = serverNow trừ giờ máy lúc nhận). Thiếu hoặc sai serverNow ⇒ giờ máy.
 */
export const estimateServerNowMs = (
  serverNow: string | null | undefined,
  receivedAtMs: number,
  nowMs: number
): number => {
  const s = serverNow ? Date.parse(serverNow) : NaN;
  return Number.isFinite(s) ? s + (nowMs - receivedAtMs) : nowMs;
};

/**
 * Số giây cần tua tới khi nạp bài. Chính lớp phủ này vừa báo bắt đầu bài (`startedHere`) ⇒ 0.
 * Còn lại tính theo giờ máy chủ, không dùng đồng hồ OBS.
 */
export const overlayResumeOffset = (o: {
  startedAt: string | null;
  serverNow: string | null | undefined;
  receivedAtMs: number;
  nowMs: number;
  maxSeconds: number;
  startedHere: boolean;
}): number => {
  if (o.startedHere) return 0;
  return resumeOffsetSeconds(
    o.startedAt,
    estimateServerNowMs(o.serverNow, o.receivedAtMs, o.nowMs),
    o.maxSeconds
  );
};

/** Tối đa 3 bài kế tiếp để hiện ở khối "Tiếp theo" (bỏ bài đang phát nếu lẫn trong danh sách). */
export const nextUpItems = (o: MusicOverlay | null, limit = 3): MusicItem[] => {
  if (!o || !o.next) return [];
  return o.next.filter((i) => i.id !== o.current?.id).slice(0, limit);
};

/** "Thứ {n} trong hàng chờ · khoảng {mm:ss} nữa" cho bài đang chờ của người xem. */
export const queueEtaLabel = (
  position: number | null | undefined,
  etaSeconds: number | null | undefined
): string => {
  if (typeof position !== 'number' || !Number.isFinite(position) || position < 1) return '';
  const head = `Thứ ${position} trong hàng chờ`;
  if (typeof etaSeconds !== 'number' || !Number.isFinite(etaSeconds)) return head;
  if (etaSeconds <= 0) return `${head} · sắp tới lượt`;
  return `${head} · khoảng ${formatMmSs(etaSeconds)} nữa`;
};

/** Chữ ngắn trên lớp phủ donate cho donate là tiền yêu cầu nhạc (tin nhắn máy chủ có dạng "🎵 tên bài"). */
export const musicDonateText = (message: string | null | undefined): string => {
  const title = (message || '').replace(/^\s*🎵\s*/u, '').trim();
  return title ? `Yêu cầu nhạc: ${title}` : 'Yêu cầu nhạc';
};

// ── Bảng mức phát nhạc ──────────────────────────────────────────────────────────────────

export const MAX_TIERS = 5;
export const MIN_TIER_SECONDS = 15;
export const MAX_TIER_SECONDS = 600;

const formatNumber = (v: number) => Number(v || 0).toLocaleString('vi-VN');

/**
 * Bảng mức của kênh, xếp theo giá tăng dần. Máy chủ cũ không trả `tiers` (hoặc trả rỗng) thì
 * dựng một mức duy nhất từ giá và thời lượng tối đa cũ.
 */
export const resolveTiers = (
  tiers: MusicTier[] | null | undefined,
  price: number,
  maxSeconds: number
): MusicTier[] => {
  if (tiers && tiers.length > 0) return [...tiers].sort((a, b) => a.price - b.price);
  return [{ price, seconds: maxSeconds }];
};

/** Mức mặc định khi người xem chưa chọn: mức giá thấp nhất. */
export const defaultTierPrice = (tiers: MusicTier[]): number | null =>
  tiers.length > 0 ? Math.min(...tiers.map((t) => t.price)) : null;

/** "{giá} Ca · phát {giây} giây" cho lựa chọn của người xem. */
export const tierChoiceLabel = (t: MusicTier): string =>
  `${formatNumber(t.price)} Ca · phát ${formatNumber(t.seconds)} giây`;

/** Số giây bài được phát: đã chốt theo mức lúc gửi, không có thì dùng trần của kênh. */
export const itemSeconds = (
  item: { seconds?: number | null } | null | undefined,
  maxSeconds: number
): number => {
  const s = item?.seconds;
  return typeof s === 'number' && Number.isFinite(s) && s > 0 ? s : maxSeconds;
};

/** "{giá} Ca · {giây} giây" cho từng bài trong hàng chờ của streamer. */
export const queueItemLabel = (
  item: { price: number; seconds?: number | null },
  maxSeconds: number
): string => `${formatNumber(item.price)} Ca · ${formatNumber(itemSeconds(item, maxSeconds))} giây`;

export interface TierRow {
  price: string;
  seconds: string;
}

/** Xếp các dòng theo giá tăng dần (dòng chưa nhập giá đứng cuối) để thứ tự gõ không chặn việc lưu. */
export const sortTierRows = (rows: TierRow[]): TierRow[] => {
  const key = (r: TierRow) => (r.price.trim() === '' || Number.isNaN(Number(r.price)) ? Infinity : Number(r.price));
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => key(a.r) - key(b.r) || a.i - b.i)
    .map((x) => x.r);
};

/** Kiểm bảng mức ở phía client, cùng câu chữ với máy chủ. Hợp lệ ⇒ null. */
export const validateTierRows = (rows: TierRow[]): string | null => {
  if (rows.length < 1 || rows.length > MAX_TIERS) return 'Cần ít nhất 1 mức và tối đa 5 mức.';
  const parsed = sortTierRows(rows).map((r) => ({
    price: r.price.trim() === '' ? NaN : Number(r.price),
    seconds: r.seconds.trim() === '' ? NaN : Number(r.seconds),
  }));
  for (const t of parsed) {
    if (!Number.isInteger(t.price) || t.price <= 0) return 'Giá phải lớn hơn 0.';
    if (!Number.isInteger(t.seconds) || t.seconds < MIN_TIER_SECONDS || t.seconds > MAX_TIER_SECONDS) {
      return `Thời lượng mỗi mức phải từ ${MIN_TIER_SECONDS} đến ${MAX_TIER_SECONDS} giây.`;
    }
  }
  for (let i = 1; i < parsed.length; i += 1) {
    if (parsed[i].price <= parsed[i - 1].price || parsed[i].seconds <= parsed[i - 1].seconds) {
      return 'Mức sau phải có giá cao hơn và thời lượng dài hơn mức trước.';
    }
  }
  return null;
};

export const rowsToTiers = (rows: TierRow[]): MusicTier[] =>
  rows.map((r) => ({ price: Number(r.price), seconds: Number(r.seconds) }));

export const tiersToRows = (tiers: MusicTier[]): TierRow[] =>
  tiers.map((t) => ({ price: `${t.price}`, seconds: `${t.seconds}` }));
